/**
 * BudgetTimeline Component
 *
 * Linha do tempo com histórico de alterações e eventos do orçamento
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Calendar,
  Clock,
  User,
  Mail,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Edit,
  Trash2,
  Download,
  Eye,
  EyeOff,
  MessageSquare,
  History,
  MoreVertical,
  Filter,
  Search,
  Plus,
  RefreshCw,
  DollarSign,
  Building,
  Phone,
  Share2,
  Copy,
  ExternalLink,
  Info,
  TrendingUp,
  TrendingDown,
  Zap,
  Bell
} from 'lucide-react';

interface TimelineEvent {
  id: string;
  type: 'created' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted' | 'edited' | 'commented' | 'shared' | 'downloaded' | 'reminder_sent';
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  details: {
    title: string;
    description?: string;
    metadata?: Record<string, any>;
    changes?: {
      field: string;
      oldValue: any;
      newValue: any;
    }[];
    attachments?: {
      name: string;
      url: string;
      size: string;
    }[];
  };
  visibility: 'public' | 'private' | 'internal';
  importance: 'low' | 'medium' | 'high' | 'critical';
}

interface BudgetTimelineProps {
  events: TimelineEvent[];
  budgetId: string;
  onAddEvent?: (event: Omit<TimelineEvent, 'id' | 'timestamp'>) => void;
  onEditEvent?: (eventId: string, event: Partial<TimelineEvent>) => void;
  onDeleteEvent?: (eventId: string) => void;
  showFilters?: boolean;
  allowAddEvents?: boolean;
  maxEvents?: number;
  className?: string;
}

const EVENT_TYPE_CONFIG = {
  created: {
    label: 'Orçamento Criado',
    icon: FileText,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Orçamento foi criado no sistema'
  },
  sent: {
    label: 'Enviado para Cliente',
    icon: Send,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Orçamento enviado ao cliente'
  },
  viewed: {
    label: 'Visualizado pelo Cliente',
    icon: Eye,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Cliente visualizou o orçamento'
  },
  approved: {
    label: 'Aprovado',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Orçamento aprovado pelo cliente'
  },
  rejected: {
    label: 'Rejeitado',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    description: 'Orçamento rejeitado pelo cliente'
  },
  expired: {
    label: 'Expirado',
    icon: Clock,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    description: 'Validade do orçamento expirou'
  },
  converted: {
    label: 'Convertido em Venda',
    icon: RefreshCw,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Orçamento convertido em venda'
  },
  edited: {
    label: 'Editado',
    icon: Edit,
    color: 'text-foreground',
    bgColor: 'bg-card',
    description: 'Orçamento foi modificado'
  },
  commented: {
    label: 'Comentário Adicionado',
    icon: MessageSquare,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Novo comentário adicionado'
  },
  shared: {
    label: 'Compartilhado',
    icon: Share2,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Orçamento compartilhado'
  },
  downloaded: {
    label: 'Baixado',
    icon: Download,
    color: 'text-foreground',
    bgColor: 'bg-card',
    description: 'PDF do orçamento baixado'
  },
  reminder_sent: {
    label: 'Lembrete Enviado',
    icon: Bell,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    description: 'Lembrete enviado ao cliente'
  }
};

const IMPORTANCE_CONFIG = {
  low: { label: 'Baixa', color: 'text-muted-foreground', bgColor: 'bg-card' },
  medium: { label: 'Média', color: 'text-blue-500', bgColor: 'bg-blue-100' },
  high: { label: 'Alta', color: 'text-orange-500', bgColor: 'bg-orange-100' },
  critical: { label: 'Crítica', color: 'text-red-500', bgColor: 'bg-red-100' }
};

const VISIBILITY_CONFIG = {
  public: { label: 'Público', icon: Eye, color: 'text-green-600' },
  private: { label: 'Privado', icon: EyeOff, color: 'text-foreground' },
  internal: { label: 'Interno', icon: Building, color: 'text-blue-600' }
};

export function BudgetTimeline({
  events,
  budgetId,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  showFilters = true,
  allowAddEvents = true,
  maxEvents,
  className
}: BudgetTimelineProps) {
  const [filterType, setFilterType] = useState<string>('all');
  const [filterImportance, setFilterImportance] = useState<string>('all');
  const [filterVisibility, setFilterVisibility] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    type: 'commented' as TimelineEvent['type'],
    title: '',
    description: '',
    visibility: 'public' as TimelineEvent['visibility'],
    importance: 'medium' as TimelineEvent['importance']
  });

  // Filter events
  const filteredEvents = events.filter(event => {
    if (filterType !== 'all' && event.type !== filterType) return false;
    if (filterImportance !== 'all' && event.importance !== filterImportance) return false;
    if (filterVisibility !== 'all' && event.visibility !== filterVisibility) return false;
    if (searchTerm && !event.details.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !event.details.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  }).slice(0, maxEvents);

  const handleAddEvent = () => {
    if (!newEvent.title.trim()) return;

    onAddEvent?.({
      type: newEvent.type,
      user: {
        id: 'current-user',
        name: 'Usuário Atual',
        email: 'usuario@empresa.com'
      },
      details: {
        title: newEvent.title,
        description: newEvent.description
      },
      visibility: newEvent.visibility,
      importance: newEvent.importance
    });

    setAddEventDialogOpen(false);
    setNewEvent({
      type: 'commented',
      title: '',
      description: '',
      visibility: 'public',
      importance: 'medium'
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `há ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `há ${hours} hora${hours !== 1 ? 's' : ''}`;
    } else if (diffInHours < 48) {
      return 'ontem';
    } else if (diffInHours < 168) {
      const days = Math.floor(diffInHours / 24);
      return `há ${days} dia${days !== 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Atividades
          </CardTitle>
          <div className="flex items-center gap-2">
            {allowAddEvents && (
              <Button size="sm" onClick={() => setAddEventDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Adicionar Evento
              </Button>
            )}
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1" />
              Exportar
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar eventos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo de evento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterImportance} onValueChange={setFilterImportance}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Importância" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(IMPORTANCE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterVisibility} onValueChange={setFilterVisibility}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Visibilidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(VISIBILITY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhuma atividade registrada</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || filterType !== 'all' || filterImportance !== 'all' || filterVisibility !== 'all'
                ? 'Nenhuma atividade encontrada com os filtros selecionados'
                : 'Este orçamento ainda não possui histórico de atividades'
              }
            </p>
            {(searchTerm || filterType !== 'all' || filterImportance !== 'all' || filterVisibility !== 'all') && (
              <Button variant="outline" onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setFilterImportance('all');
                setFilterVisibility('all');
              }}>
                Limpar Filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredEvents.map((event, index) => {
              const config = EVENT_TYPE_CONFIG[event.type];
              const Icon = config.icon;
              const importanceConfig = IMPORTANCE_CONFIG[event.importance];
              const visibilityConfig = VISIBILITY_CONFIG[event.visibility];
              const VisibilityIcon = visibilityConfig.icon;

              return (
                <div key={event.id} className="flex gap-4">
                  {/* Timeline Line */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${config.color}`} />
                    </div>
                    {index < filteredEvents.length - 1 && (
                      <div className="w-0.5 h-20 bg-border mt-2" />
                    )}
                  </div>

                  {/* Event Content */}
                  <div className="flex-1 min-w-0">
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{event.details.title}</h4>
                            <Badge
                              variant="outline"
                              className={`text-xs ${importanceConfig.color} ${importanceConfig.bgColor}`}
                            >
                              {importanceConfig.label}
                            </Badge>
                            {event.visibility !== 'public' && (
                              <Badge
                                variant="outline"
                                className={`text-xs ${visibilityConfig.color}`}
                              >
                                <VisibilityIcon className="h-3 w-3 mr-1" />
                                {visibilityConfig.label}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {config.description}
                          </p>
                          {event.details.description && (
                            <p className="text-sm mb-3">{event.details.description}</p>
                          )}

                          {/* Changes Display */}
                          {event.details.changes && event.details.changes.length > 0 && (
                            <div className="bg-muted rounded border p-3 mb-3">
                              <h5 className="text-sm font-medium mb-2">Alterações realizadas:</h5>
                              <div className="space-y-1">
                                {event.details.changes.map((change, idx) => (
                                  <div key={idx} className="text-sm">
                                    <span className="font-medium">{change.field}:</span>
                                    <span className="text-red-600 ml-1 line-through">
                                      {change.oldValue}
                                    </span>
                                    <span className="mx-1">→</span>
                                    <span className="text-green-600 font-medium">
                                      {change.newValue}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Attachments */}
                          {event.details.attachments && event.details.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {event.details.attachments.map((attachment, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  <Download className="h-3 w-3 mr-1" />
                                  {attachment.name}
                                </Badge>
                              ))}
                            </div>
                          )}

                          {/* Metadata */}
                          {event.details.metadata && Object.keys(event.details.metadata).length > 0 && (
                            <div className="text-xs text-muted-foreground space-y-1">
                              {Object.entries(event.details.metadata).map(([key, value]) => (
                                <div key={key}>
                                  <span className="font-medium capitalize">
                                    {key.replace('_', ' ')}:
                                  </span>{' '}
                                  {typeof value === 'object' ? JSON.stringify(value) : value}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Copy className="h-4 w-4 mr-2" />
                              Copiar Detalhes
                            </DropdownMenuItem>
                            {event.details.attachments && event.details.attachments.length > 0 && (
                              <DropdownMenuItem>
                                <Download className="h-4 w-4 mr-2" />
                                Baixar Anexos
                              </DropdownMenuItem>
                            )}
                            {onEditEvent && (
                              <DropdownMenuItem onClick={() => onEditEvent(event.id, {})}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar Evento
                              </DropdownMenuItem>
                            )}
                            {onDeleteEvent && (
                              <DropdownMenuItem
                                onClick={() => onDeleteEvent(event.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir Evento
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* User and Timestamp */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={event.user.avatar} />
                            <AvatarFallback className="text-xs">
                              {event.user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{event.user.name}</p>
                            <p className="text-xs text-muted-foreground">{event.user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground" title={formatDateTime(event.timestamp)}>
                            {formatDate(event.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {maxEvents && events.length > maxEvents && (
              <div className="text-center pt-4">
                <Button variant="outline" size="sm">
                  Carregar Mais Atividades
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Add Event Dialog */}
      <Dialog open={addEventDialogOpen} onOpenChange={setAddEventDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Evento ao Histórico</DialogTitle>
            <DialogDescription>
              Registre uma nova atividade ou anotação para este orçamento
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Evento</Label>
                <Select
                  value={newEvent.type}
                  onValueChange={(value: TimelineEvent['type']) => setNewEvent({ ...newEvent, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importância</Label>
                <Select
                  value={newEvent.importance}
                  onValueChange={(value: TimelineEvent['importance']) => setNewEvent({ ...newEvent, importance: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(IMPORTANCE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Título *</Label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="Título do evento"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Detalhes adicionais sobre este evento"
                rows={3}
              />
            </div>

            <div>
              <Label>Visibilidade</Label>
              <Select
                value={newEvent.visibility}
                onValueChange={(value: TimelineEvent['visibility']) => setNewEvent({ ...newEvent, visibility: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VISIBILITY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEventDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddEvent} disabled={!newEvent.title.trim()}>
              Adicionar Evento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}